import { IAssemblyData } from '@/editor/data/assembly/assembly';
import { IFurniture } from '@/editor/data/model/furniture';
import { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import FurnitureEntity from '@/editor/entities/furnitureEntity';
import { createPlacementMapper } from '@/editor/math/utils';
import { modelManager } from '@/editor/manager/entityManager';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { MULTI_ID_SUFFIX } from '@/editor/controller/helper/constants';

/** 多选移动的虚拟家具实体集合（组合展开成子家具），虚拟实体 id 加 MULTI_ID_SUFFIX 后缀 */
export class VirtualMultiModelEntities {
  private _entityMap: Map<string, FurnitureEntity[]> = new Map();
  private _furnitureIds: string[] = [];
  private _assemblyIds: string[] = [];

  /** 含组合展开的子家具 id，供隐藏真实实体与吸附 exclude 复用 */
  get furnitureIds() {
    return this._furnitureIds;
  }

  get assemblyIds() {
    return this._assemblyIds;
  }

  create(multiModelData: IMultiModelData) {
    const furnitureIds: string[] = [];
    const assemblyIds: string[] = [];

    for (const modelInfo of multiModelData.modelInfos) {
      const checkedData = globalStores.getDataByIdType(modelInfo.id, modelInfo.type) as
        | (IFurniture | IAssemblyData)
        | null;
      if (!checkedData) continue;

      if (modelInfo.type === EEntityType.Furniture) {
        const virtualFurnitureEntity = new FurnitureEntity({ id: checkedData.id + MULTI_ID_SUFFIX });
        virtualFurnitureEntity.update(checkedData);
        this._entityMap.set(checkedData.id, [virtualFurnitureEntity]);

        furnitureIds.push(checkedData.id);
      } else if (modelInfo.type === EEntityType.Assembly) {
        const furnitureEntities: FurnitureEntity[] = [];

        for (const accessorieId of (checkedData as IAssemblyData).accessorieIds) {
          const furnitureData = globalStores.getDataByIdType(accessorieId, EEntityType.Furniture);
          if (!furnitureData) continue;

          const virtualFurnitureEntity = new FurnitureEntity({ id: furnitureData.id + MULTI_ID_SUFFIX });
          virtualFurnitureEntity.update({ ...furnitureData, selected: false });
          furnitureEntities.push(virtualFurnitureEntity);

          furnitureIds.push(furnitureData.id);
        }
        assemblyIds.push(checkedData.id);
        this._entityMap.set(checkedData.id, furnitureEntities);
      }
    }

    this._furnitureIds = furnitureIds;
    this._assemblyIds = assemblyIds;
  }

  update(movedData: IMultiModelData) {
    for (const modelInfo of movedData.modelInfos) {
      const entities = this._entityMap.get(modelInfo.id);
      if (!entities || !entities.length) continue;

      if (modelInfo.type === EEntityType.Furniture) {
        entities[0].update(modelInfo.data);
      } else if (modelInfo.type === EEntityType.Assembly) {
        if (entities.length === modelInfo.subModeldatas.length) {
          for (let i = 0; i < entities.length; i++) {
            entities[i].update(modelInfo.subModeldatas[i]);
          }
        }
      }
    }
  }

  setRealEntitiesVisible(visible: boolean) {
    for (const id of this._furnitureIds) {
      const entity = modelManager.getEntity(id, EEntityType.Furniture);
      if (!entity) continue;
      entity.setVisible(visible);
    }
  }

  dispose() {
    for (const entities of this._entityMap.values()) {
      for (const entity of entities) {
        entity.dispose();
      }
    }
    this._entityMap.clear();
    this._furnitureIds = [];
    this._assemblyIds = [];
  }
}

/** 组合移动的子家具虚拟实体，虚拟实体 id 加 'assembly' 后缀 */
export class VirtualAssemblyEntities {
  private _entityMap: Map<string, FurnitureEntity> = new Map();

  /** 把子家具从原组合坐标系映射到移动后组合坐标系，并更新虚拟实体 */
  update(originData: IAssemblyData, movedData: IAssemblyData) {
    const mapPlacement = createPlacementMapper(originData, movedData);

    const furnitureDatas = globalStores.assemblyOperation.getAssemblyFurnitureDatas(originData);
    for (const furnitureData of furnitureDatas) {
      const { center, rotation } = mapPlacement(furnitureData);
      furnitureData.center = center;
      furnitureData.rotation = rotation;

      let furnitureEntity = this._entityMap.get(furnitureData.id);
      if (!furnitureEntity) {
        furnitureEntity = new FurnitureEntity({ id: furnitureData.id + 'assembly' });
        this._entityMap.set(furnitureData.id, furnitureEntity);
      }
      furnitureEntity.update(furnitureData);
    }
  }

  dispose() {
    for (const entity of this._entityMap.values()) {
      entity.dispose();
    }
    this._entityMap.clear();
  }
}
